import {
  call,
  cancelled,
  put,
  race,
  take,
  takeEvery,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects';
import { apiRequest, getToken, setToken } from 'utils/request';
import { authProviderActions as actions } from '.';

const ignoreActionTypes = () => [
  actions.userTokenRefreshRequest.type,
  actions.userLogoutRequest.type,
];

function monitorableAction(action: { type: string }) {
  return (
    action.type.includes('Request') &&
    ignoreActionTypes().every(fragment => !action.type.includes(fragment))
  );
}

function identifyAction(action) {
  return action.type.slice(0, -7);
}
function getSuccessType(action) {
  return `${identifyAction(action)}Success`;
}
function getFailType(action) {
  return `${identifyAction(action)}Failed`;
}

function* monitor(monitoredAction) {
  const { fail } = yield race({
    success: take(getSuccessType(monitoredAction)),
    fail: take(getFailType(monitoredAction)),
  });

  if (fail && fail.payload && fail.payload.code === 403) {
    // yield put(actions.userLogoutRequest());
  }

  if (fail && fail.payload && fail.payload.code === 401) {
    if (fail.payload.message === 'Token not provided') {
      yield put(actions.userLogoutRequest());
    }
    if (getToken()) {
      yield put(actions.userTokenRefreshRequest());
      const { success } = yield race({
        success: take(action => actions.userTokenRefreshSuccess.match(action)),
        fail: take(action => actions.userTokenRefreshFailed.match(action)),
      });

      if (success) {
        yield put(monitoredAction);
      } else {
        yield put(actions.userLogoutRequest());
      }
    }
  }
}

function* login(action: {
  type: string;
  payload: { email: string; password: string; redirect?: string };
}) {
  try {
    const { email, password, redirect } = action.payload;
    const payload = yield call(apiRequest, {
      url: 'auth/login',
      method: 'post',
      data: {
        email,
        password,
        redirect,
      },
    });
    if (payload.token) {
      // if token is set here - which will only happen when the user is set
      // to google2fa_disabled = true OR the env has disabled it
      // set token here.
      yield call(setToken, payload.token);
      yield put(actions.loadUserDataRequest());
      yield put(
        actions.userLoginSuccess({
          payload: {
            login: true,
          },
        }),
      );
    } else {
      yield put(actions.userLoginSuccess(payload));
    }
  } catch (error) {
    // @ts-ignore
    yield put(actions.userLoginFailed(error.payload));
  } finally {
    if (yield cancelled()) {
    }
  }
}

function* authenticate2fa({ payload: { validationToken, key } }) {
  let response;
  try {
    // try to call to our loginApi() function.  Redux Saga
    // will pause here until we either are successful or
    // receive an error
    response = yield call(apiRequest, {
      url: 'auth/validate_2fa',
      method: 'post',
      data: {
        validation_token: validationToken,
        key,
      },
    });

    if (response.token) {
      // if token is set here - log user in
      yield call(setToken, response.token);
      yield put(actions.loadUserDataRequest());
      yield put(
        actions.userLoginSuccess({
          login: true,
        }),
      );
      yield put(actions.authenticate2faSuccess(response));
    } else {
      throw new Error('Google 2FA did not return token');
    }
  } catch (error) {
    console.log(error);
    // @ts-ignore
    yield put(actions.authenticate2faFailed(error.payload));
  } finally {
    if (yield cancelled()) {
      // TODO - if needed
    }
  }
}

function* autoLogin(action: { type: string; payload: { key: string } }) {
  try {
    const { key } = action.payload;
    const payload = yield call(apiRequest, {
      url: 'auth/autologin',
      method: 'post',
      data: {
        key,
      },
    });

    if (payload.token) {
      yield call(setToken, payload.token);
    }
    yield put(actions.loadUserDataRequest());
    yield put(actions.autoLoginSuccess({}));
  } catch (error) {
    yield put(actions.autoLoginFailed(error.payload));
  } finally {
    if (yield cancelled()) {
    }
  }
}

function* logout(action) {
  try {
    yield call(apiRequest, {
      url: 'auth/logout',
      method: 'post',
    });
    yield call(setToken, undefined);
    yield put(actions.userLogoutSuccess());
  } catch (error) {
    yield put(actions.userLogoutFailed(error.payload));
    yield call(setToken, undefined);
  } finally {
    if (yield cancelled()) {
    }
  }
}

function* forgotPassword(action: { type: string; payload: { email: string } }) {
  try {
    const { email } = action.payload;
    const payload = yield call(apiRequest, {
      url: 'auth/recovery',
      method: 'post',
      data: {
        email,
      },
    });

    yield put(actions.forgotPasswordSuccess(payload));
  } catch (error) {
    yield put(actions.forgotPasswordFailed(error.payload));
  } finally {
    if (yield cancelled()) {
    }
  }
}

function* resetPassword(action: {
  type: string;
  payload: {
    email: string;
    token: string;
    password: string;
    password_confirmation: string;
  };
}) {
  try {
    const { email, token, password, password_confirmation } = action.payload;
    const payload = yield call(apiRequest, {
      url: 'auth/reset',
      method: 'post',
      data: {
        email,
        token,
        password,
        password_confirmation,
      },
    });
    if (payload.token) {
      yield call(setToken, payload.token);
      yield put(actions.loadUserDataRequest());
    }

    yield put(actions.resetPasswordSuccess(payload));
  } catch (error) {
    yield put(actions.resetPasswordFailed(error.payload));
  } finally {
    if (yield cancelled()) {
    }
  }
}

function* loadUserData(action) {
  try {
    const payload = yield call(apiRequest, {
      url: 'auth/me',
      method: 'get',
    });
    yield put(actions.loadUserDataSuccess(payload));
  } catch (error) {
    yield put(actions.loadUserDataFailed(error.payload));
  } finally {
    if (yield cancelled()) {
    }
  }
}

function* tokenRefresh(action) {
  try {
    const payload = yield call(apiRequest, {
      url: 'auth/refresh',
      method: 'post',
    });
    if (payload.token) {
      yield call(setToken, payload.token);
    }
    yield put(actions.userTokenRefreshSuccess());
  } catch (error) {
    yield put(actions.userTokenRefreshFailed(error.payload));
  } finally {
    if (yield cancelled()) {
    }
  }
}

export function* authProviderSaga() {
  yield takeEvery(monitorableAction, monitor);
  yield takeLeading(actions.userLoginRequest.type, login);
  yield takeLeading(actions.autoLoginRequest.type, autoLogin);
  yield takeLeading(actions.userTokenRefreshRequest.type, tokenRefresh);
  yield takeLatest(actions.userLogoutRequest.type, logout);
  yield takeLatest(actions.loadUserDataRequest.type, loadUserData);
  yield takeLatest(actions.forgotPasswordRequest.type, forgotPassword);
  yield takeLatest(actions.resetPasswordRequest.type, resetPassword);

  yield takeLatest(actions.authenticate2faRequest.type, authenticate2fa);
}
